import { createAsyncThunk, createSelector, createSlice, PayloadAction, SerializedError, type Draft } from "@reduxjs/toolkit";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { RootState } from "_shared/_stores/redux-store";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { messagingApi } from "messaging/_api/messaging-api";
import MessageParameters, { MessageFilterParameters } from "messaging/_api/queries/message-parameters";
import MessageQuery from "messaging/_api/queries/message-query";
import Message from "messaging/message";
import ReadReceipt from "messaging/read-receipts/read-receipt";
import Session from "users/__session/session";
import Comment from "work/proposals/comments/comment";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { proposalCommentsPublishOnPost } from "work/proposals/comments/proposal-comment-publishing";
import Proposal from "work/proposals/proposal";

/**
 * Comments keyed by the proposal (negotiation) they belong to.
 *
 * They used to be keyed per field, because a proposal had a comment forum per field. It has one per
 * audience now — see {@link CommentThread} — and the pane shows them as a single stream, so the
 * proposal is the whole key.
 */
type CommentStoreState = {
  entries: Record<string, Comment[]>;
  loading: Record<string, boolean>;
  error: Record<string, SerializedError | null>;
  /**
   * Whether each conversation's own comments loaded, by forum id (#6631). `entries` is one list for both
   * of a negotiation's conversations, so it says a load happened, not which conversation it was for: one
   * conversation's comments, or a comment arriving live, fill it while the other is still loading.
   */
  forumLoads: Record<string, ForumCommentsLoad>;
};

/** A conversation whose first page of comments arrived, or whose load failed before any did. */
export type ForumCommentsLoad = "loaded" | "failed";

type MarkCommentsReadArgs = {
  session: Session;
  /** The negotiation the comments are filed under: the comment store's key. */
  proposalId: Guid;
  commentIds: readonly string[];
};

const initialState: CommentStoreState = {
  entries: {},
  loading: {},
  error: {},
  forumLoads: {},
};

const selectCommentEntries = (state: RootState) => state.comments.entries;

/** Whether one conversation's comments loaded, or failed to: undefined until either happens. */
export const getForumCommentsLoad =
  (forumId: string | null | undefined) =>
  (state: RootState): ForumCommentsLoad | undefined =>
    forumId ? state.comments.forumLoads?.[forumId] : undefined;

const pendingCommentsCountSelectorCache = new Map<string, (state: RootState) => number>();

function countMatchingComments(comments: Comment[] | undefined, predicate: (comment: Comment) => boolean): number {
  if (!comments?.length) {
    return 0;
  }

  let count = 0;
  for (const comment of comments) {
    if (predicate(comment)) {
      count += 1;
    }
  }

  return count;
}

/**
 * A persisted author-only draft, as against one its author published once and then took back.
 *
 * Only meaningful with drafting configured on ({@link proposalCommentsPublishOnPost} false): a comment
 * posts published, so a draft is something the author chose. It is what the pane's draft affordances key
 * off — a draft offers Publish, a retracted one offers it too but is never swept up by anything acting on
 * "the author's drafts" as a group.
 */
function isPublishableDraft(comment: Pick<Comment, "isUnpublishedDraft" | "isRetractedDraft">): boolean {
  return comment.isUnpublishedDraft && !comment.isRetractedDraft;
}

/**
 * A thread's newest page of comments. Oldest first, page 0 of a thread of more than one page held its
 * oldest comments, and the newest one was never loaded (#6769). `loadCommentsThroughOldestUnread`
 * loads the older pages.
 */
function buildDefaultThreadCommentsQuery(): MessageQuery {
  return new MessageQuery(
    new MessageParameters(new MessageFilterParameters({ newestFirst: true })),
    PaginationParameters.create({
      pageIndex: 0,
      pageSize: 100,
    }),
  );
}

/** The store key a proposal's comments live under. */
function commentStoreKey(proposalId: Guid): string {
  return proposalId.value;
}

/** The distinct proposal keys a batch of threads covers, for setting their shared loading/error state. */
function requestedProposalKeys(threads: CommentThread[]): string[] {
  return [...new Set(threads.map((thread) => commentStoreKey(thread.proposalId)))];
}


function upsertCommentInEntries(state: Draft<CommentStoreState>, comment: Comment): void {
  const proposalId = comment.thread.proposalId;
  if (!proposalId) {
    return;
  }

  const proposalKey = commentStoreKey(proposalId);
  const existingComments =
    state.entries[proposalKey]?.filter((existingComment) => !existingComment.isEqualTo(comment)) ?? [];

  existingComments.push(comment);
  state.entries[proposalKey] = existingComments;
}

/** Records a failed load of a conversation, unless an earlier one already loaded it. */
function markForumLoadFailed(state: Draft<CommentStoreState>, forumId: string | undefined): void {
  if (!forumId || state.forumLoads[forumId] === "loaded") return;
  state.forumLoads[forumId] = "failed";
}

/** Draws every loaded comment of one conversation read for the reader, after Mark all read. */
function markLoadedThreadReadInEntries(state: Draft<CommentStoreState>, forumId: string, readerId: Guid): void {
  const seenAt = new AHDate();
  for (const proposalKey of Object.keys(state.entries)) {
    const loadedComments = state.entries[proposalKey] as Comment[] | undefined;
    if (!loadedComments?.some((comment) => comment.thread.id?.value === forumId)) continue;
    state.entries[proposalKey] = loadedComments.map((comment) =>
      comment.thread.id?.value === forumId && comment.isUnreadBy(readerId)
        ? comment.with({ readReceipts: [...comment.readReceipts, new ReadReceipt({ userId: readerId, seenAt })] })
        : comment,
    );
  }
}

/**
 * Applies a read-receipt write's answer WITHOUT letting it restate the rest of the comment.
 *
 * The write's response is a whole comment, and it is only authoritative for the one thing the write
 * asked about. It is a snapshot of the row as the server found it, which is not necessarily the row as
 * it stands: two writes to one comment can be in flight at once, and this endpoint answers each with
 * everything, so upserting the whole payload makes the LAST response to arrive win on every field
 * rather than on the field it changed. That cost a retraction — a sweep that fired on opening the pane,
 * the author retracted while it was still out, and the sweep's answer landed 62ms later carrying
 * `retracted: null` and put the comment back on its audience's side of the fence in the store. The row
 * read as published, offered no Publish button, and stayed that way until a reload, while the server
 * row was retracted the whole time. (CircleCI 54188/54218; `Comment.isUnreadBy` no longer sweeps the
 * reader's own comments, which is what made that particular pair collide, but the race is not the
 * author's own comments — it is applying a whole payload for a partial write.)
 *
 * Taking only the receipts leaves whatever else has happened to the comment since alone. The two
 * read-state marks are cleared because this response is what settles them: they are the local record
 * that a receipt was owed, and it has now been written.
 */
function applyReadReceiptsInEntries(state: Draft<CommentStoreState>, answered: Comment): void {
  const existing = state.entries[commentStoreKey(answered.thread.proposalId)]?.find((comment) =>
    comment.isEqualTo(answered),
  );

  // Only a comment the pane had loaded is ever marked read (#5824). One that is gone by the time the
  // answer lands was removed while the receipt was on the wire, and the answer must not put it back.
  if (!existing) return;
  upsertCommentInEntries(
    state,
    existing.with({
      readReceipts: [...answered.readReceipts],
      // Undefined, not false: `isPending` tests these against `undefined`, so a settled write recorded
      // as `false` would leave the comment advertising outstanding work for ever.
      markedForReadReceipt: undefined,
      markedForUnread: undefined,
    }),
  );
}

/**
 * Applies the server's answer to a comment's creation — the bulk endpoint's response to the post, or the
 * realtime broadcast of the same write — WITHOUT letting it restate a comment the store has already
 * heard about from the server.
 *
 * A creation is answered twice, and the response is the slow one: the row is stored and broadcast when
 * the write commits, and the response is built only once the notifications it raised have committed
 * too. The broadcast reached the author's own pane 470ms before the response did, and in that window
 * the pane showed a stored, published comment with its full set of controls. The author retracted it,
 * the retraction's answer landed with `retracted` set, and 24ms later the creation's answer landed
 * carrying `retracted: null` — the row as the server had stored it, by then one write out of date — and
 * put the comment back on its audience's side of the fence. The row read as published and offered no
 * Publish button, while the server row was retracted the whole time. (CircleCI 56070/56277/56420; the
 * same class as {@link applyReadReceiptsInEntries}, from the other end: not a partial write answered
 * with a whole payload, but a whole payload that is a snapshot of an earlier write.)
 *
 * A creation is authoritative for exactly one transition: from this tab's own unacknowledged draft to
 * the server's copy of it. Once the store holds a server copy — from the broadcast, from the response's
 * twin, or from a later write's answer — the creation has nothing left to say, and applying it could
 * only roll the comment back to the instant it was stored. The audience's tab has no draft to replace,
 * so a comment somebody else wrote goes in as it stands.
 */
function applyCreationAnswerInEntries(state: Draft<CommentStoreState>, stored: Comment): void {
  const existing = state.entries[commentStoreKey(stored.thread.proposalId)]?.find((comment) =>
    comment.isEqualTo(stored),
  );
  if (existing && !existing.markedForCreation) return;

  upsertCommentInEntries(state, stored);
}

export const populateThreadComments = createAsyncThunk(
  "comments/getCommentsByThread",
  async (
    {
      session,
      thread,
      query,
    }: {
      session: Session;
      thread: CommentThread;
      // Optional: callers that just want a thread's comments loaded (eager pre-load, mark-read) need
      // not build the default first-page query themselves.
      query?: MessageQuery;
    },
    thunkAPI,
  ) => {
    try {
      const forum = thread.toForum({
        isViewingAsVendor: session.context?.viewingAsVendor ?? false,
      });
      if (!forum.id) {
        throw new Error("Forum must have an id to get comments");
      }
      // Released once the messages are copied into this slice: nothing reads the RTK Query cache entry
      // afterwards, and a retained subscription makes the query refetch on every `Message` LIST
      // invalidation for the life of the page. Every comment write invalidates that tag, so each save or
      // read receipt re-fetched one forum's messages per thread ever loaded — answers no reducer
      // consumes, since this thunk has long since settled.
      const request = thunkAPI.dispatch(
        messagingApi.endpoints.getMessagesByForum.initiate({
          forumId: forum.id.value,
          query: query ?? buildDefaultThreadCommentsQuery(),
          append: false,
        }),
      );
      try {
        const commentMessages = await request.unwrap();
        return commentMessages.data.map((message: Message) => Comment.fromMessage(message));
      } finally {
        request.unsubscribe();
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

/**
 * Batch sibling of `populateThreadComments`: one `getMessagesByForums` request loads every thread's
 * comment bodies instead of one `getMessagesByForum` request per thread. Each returned message still
 * carries its own embedded forum, so `Comment.fromMessage` reconstructs the same `CommentThread` per
 * comment it always has — this thunk differs only in how the messages are fetched, not in what a
 * comment becomes.
 */
export const populateThreadsComments = createAsyncThunk(
  "comments/getCommentsByThreads",
  async (
    {
      session,
      threads,
      query,
    }: {
      session: Session;
      threads: CommentThread[];
      query?: MessageQuery;
    },
    thunkAPI,
  ) => {
    try {
      const forumIds = threads.map((thread) => {
        const forum = thread.toForum({ isViewingAsVendor: session.context?.viewingAsVendor ?? false });
        if (!forum.id) {
          throw new Error("Forum must have an id to get comments");
        }
        return forum.id;
      });
      if (forumIds.length === 0) return { comments: [], loadedForumIds: [] };

      // Released for the same reason as in `populateThreadComments` above — a retained subscription
      // re-runs this batch on every `Message` LIST invalidation, so each comment write re-fetched the
      // whole negotiation's messages into a cache entry nothing reads.
      const request = thunkAPI.dispatch(
        messagingApi.endpoints.getMessagesByForums.initiate({
          forumIds,
          query: query ?? buildDefaultThreadCommentsQuery(),
        }),
      );
      let messagesByForumId: Record<string, Message[]>;
      try {
        // A thread the reader may no longer read, or one deleted since the proposal named it, is
        // answered in `unavailable` and has no comments to show.
        ({ messagesByForumId } = await request.unwrap());
      } finally {
        request.unsubscribe();
      }

      return {
        comments: Object.values(messagesByForumId)
          .flat()
          .map((message: Message) => Comment.fromMessage(message)),
        // Only the forums the server answered: one it answered as unavailable did not load.
        loadedForumIds: Object.keys(messagesByForumId),
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

/**
 * Marks the named comments read for the reader: the ones the Comments pane has scrolled into view
 * (#5824). Nothing else marks a conversation read but Mark all read, which is the open tab's own.
 *
 * Opening the pane, arriving at a traverser stop, or focusing the composer once swept every unread
 * comment in both conversations read. That retired the comments on the tab the reader was not on, which
 * nobody had seen.
 *
 * A comment that is no longer unread by the time this runs — read on another tab of the browser, or
 * already on its way — is left out, so a burst of rows coming into view writes each receipt once.
 */
export const markCommentsRead = createAsyncThunk(
  "comments/markCommentsRead",
  async ({ session, proposalId, commentIds }: MarkCommentsReadArgs, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const ids = new Set(commentIds);
      const receipts = (state.comments.entries[commentStoreKey(proposalId)] ?? [])
        // A tombstone and the reader's own comment are never unread — see `Comment.isUnreadBy`, the one
        // place that rule lives so this write, the row's dot and the tab's count cannot disagree.
        .filter((comment) => comment.id && ids.has(comment.id.value) && comment.isUnreadBy(session.user?.id))
        .map((comment) => comment.markForReadReceipt());
      if (!receipts.length) return [];

      const returnedMessages = await thunkAPI
        .dispatch(
          messagingApi.endpoints.createBulkMessages.initiate({
            messages: receipts.map((comment) =>
              comment.toMessage({ isViewingAsVendor: session.context?.viewingAsVendor ?? false }),
            ),
          }),
        )
        .unwrap();

      return returnedMessages?.map(Comment.fromMessage) ?? [];
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

/**
 * Persists a single comment through the bulk-message endpoint and returns the server copy. The comment's
 * mark flags decide what the backend does: `markedForCreation` (a fresh comment) is stored as an
 * author-only draft (publish:false), `markForPublish` publishes it, `markForUnpublish` retracts a
 * published comment back to a draft. Shared by the draft/publish/unpublish thunks below.
 */
async function persistSingleComment(
  comment: Comment,
  session: Session,
  thunkAPI: { dispatch: (action: unknown) => { unwrap: () => Promise<Message[]> } },
): Promise<Comment | undefined> {
  const message = comment.toMessage({ isViewingAsVendor: session.context?.viewingAsVendor ?? false });
  const returned = await thunkAPI
    .dispatch(messagingApi.endpoints.createBulkMessages.initiate({ messages: [message] }))
    .unwrap();
  const [saved] = returned ?? [];
  return saved ? Comment.fromMessage(saved) : undefined;
}

/**
 * Persist a newly authored comment.
 *
 * By default it is published to its audience straight away — see {@link proposalCommentsPublishOnPost}.
 * With drafting configured on instead, it is stored as an author-only draft that the row's own Publish
 * button delivers, which is what {@link saveDraftComment} does directly.
 */
export const postComment = createAsyncThunk(
  "comments/postComment",
  async ({ session, comment }: { session: Session; comment: Comment }, thunkAPI) => {
    try {
      const authored = comment.markForCreation();
      return await persistSingleComment(
        proposalCommentsPublishOnPost ? authored.markForPublish() : authored,
        session,
        thunkAPI,
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

/** Immediately persist a newly authored comment as an author-only draft (not published). */
export const saveDraftComment = createAsyncThunk(
  "comments/saveDraftComment",
  async ({ session, comment }: { session: Session; comment: Comment }, thunkAPI) => {
    try {
      return await persistSingleComment(comment.markForCreation(), session, thunkAPI);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

/** Publish a single draft comment to its audience. */
export const publishComment = createAsyncThunk(
  "comments/publishComment",
  async ({ session, comment }: { session: Session; comment: Comment }, thunkAPI) => {
    try {
      return await persistSingleComment(comment.markForPublish(), session, thunkAPI);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

/**
 * Persist an edit to an existing comment.
 *
 * An edit used to be staged — `markForEdit` set a flag, a chip read "Pending Edit", and the page Save
 * put it on the wire — which made a comment's own row depend on a proposal save the author might never
 * make. The row's Save button is the author saying so about this comment, so it is what writes it.
 *
 * The comment is sent as the caller composed it, including the thread it is now aimed at: re-aiming an
 * internal comment at the counterparty clears the thread id, so the server get-or-creates the external
 * Forum and the comment lands on the audience its author chose. Nothing about the audience is decided
 * here — it is decided by the author before this is dispatched, and abandoned with the edit if they
 * cancel.
 */
export const editComment = createAsyncThunk(
  "comments/editComment",
  async ({ session, comment }: { session: Session; comment: Comment }, thunkAPI) => {
    try {
      // An edit says nothing about who may read the comment, so it must not carry a publish decision:
      // the server treats "retract" as an alternative to updating the content, and an edit that also
      // asked to unpublish would be discarded in favour of the retraction. Publishing and retracting are
      // their own buttons.
      const edited = comment.with({
        markedForEdit: false,
        markedForPublish: false,
        markedForUnpublish: false,
      });
      const saved = await persistSingleComment(edited, session, thunkAPI);

      // Re-aiming a comment at the counterparty re-homes it: the thread id is cleared, so the server
      // resolves the external Forum, retires the internal copy and answers with a NEW comment. Drop the
      // one it replaced, or the pane shows the comment twice — once on each audience — and the internal
      // copy is the one the author believed they had moved.
      if (saved && comment.id && !saved.id?.isEqualTo(comment.id)) {
        thunkAPI.dispatch(removeCommentById(comment.id));
      }
      return saved;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

/**
 * Delete a comment outright rather than staging it.
 *
 * Deletion used to be staged for an already-published comment: a "Pending Deletion" chip held it until
 * the author saved the proposal, so the audience kept reading a comment its author had already deleted
 * for as long as the author never pressed Save — and a staged deletion the server could not match
 * re-sent itself on every subsequent save. An author-only draft was exempt, because it has no audience
 * to protect. There is no longer a save to wait for: pressing Delete deletes, for both.
 *
 * A comment the server has never seen (`markedForCreation`, no round-trip yet) is simply dropped
 * locally — asking the server to delete a row it never stored is what left deletions stuck in the store.
 *
 * A comment the server HAS seen leaves a tombstone rather than vanishing. The delete answers with the
 * comment narrowed to its metadata — author, thread, timestamps, attachment count, and nothing it said —
 * and that is what goes back into the store, so the author sees the same "This comment was deleted" row
 * their audience does instead of watching it disappear only from their own screen.
 */
export const deleteComment = createAsyncThunk(
  "comments/deleteComment",
  async ({ session, comment }: { session: Session; comment: Comment }, thunkAPI) => {
    try {
      // No id means the comment only ever existed in this tab; there is nothing for the server to
      // delete, and nothing anyone else was ever shown to leave a tombstone for either.
      if (!comment.id || comment.markedForCreation) {
        thunkAPI.dispatch(removeComment(comment));
        return comment.id;
      }

      const tombstone = await persistSingleComment(comment.setDeleted(), session, thunkAPI);
      if (tombstone) {
        thunkAPI.dispatch(addComment(tombstone));
      } else {
        // The server saved nothing it could describe back. Dropping the row is the honest fallback:
        // a tombstone invented locally would claim a deletion the store cannot confirm.
        thunkAPI.dispatch(removeComment(comment));
      }
      return comment.id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

/** Retract a single already-published comment back to an author-only draft. */
export const unpublishComment = createAsyncThunk(
  "comments/unpublishComment",
  async ({ session, comment }: { session: Session; comment: Comment }, thunkAPI) => {
    try {
      return await persistSingleComment(comment.markForUnpublish(), session, thunkAPI);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

const commentsSlice = createSlice({
  name: "comments",
  initialState,
  reducers: {
    addComment: (state, action: PayloadAction<Comment>) => {
      upsertCommentInEntries(state, action.payload);
    },
    /** A comment as its creation stored it. Not `addComment`: see {@link applyCreationAnswerInEntries}. */
    addCreatedComment: (state, action: PayloadAction<Comment>) => {
      applyCreationAnswerInEntries(state, action.payload);
    },
    removeCommentById: (state, action: PayloadAction<Guid>) => {
      for (const proposalKey of Object.keys(state.entries)) {
        const loadedComments = state.entries[proposalKey];
        if (!loadedComments) continue;
        state.entries[proposalKey] = loadedComments.filter((comment) => !comment.id?.isEqualTo(action.payload));
      }
    },
    removeComment: (state, action: PayloadAction<Comment>) => {
      const proposalId = action.payload.thread.proposalId;
      if (!proposalId) {
        return;
      }
      const proposalKey = commentStoreKey(proposalId);
      const loadedComments = state.entries[proposalKey];
      if (!loadedComments) return;

      state.entries[proposalKey] = loadedComments.filter((comment) => !comment.isEqualTo(action.payload));
    },

    clearUnsavedCommentsByProposalId(state, action: PayloadAction<Guid>) {
      const proposalKey = commentStoreKey(action.payload);
      const loadedComments = state.entries[proposalKey];
      // A proposal whose comments were never loaded has no key here, and it must not gain one: assigning
      // the result of an optional filter stored `undefined` under the key, which every selector that
      // flattens `entries` then dereferenced — `Object.values(entries).flat().some((comment) =>
      // comment.thread…)` threw "Cannot read properties of undefined (reading 'thread')" and took the
      // whole proposal page down with it. Leaving an unloaded proposal alone is also what it means:
      // there is no unsaved local work to clear.
      if (!loadedComments) return;

      state.entries[proposalKey] = loadedComments.filter((comment) => Boolean(comment.created));
    },
    // `updateThreadSubscribers` used to live here: the proposal Save re-derived the participant roster
    // from the current projection and rewrote it onto every comment still staged in the store, because a
    // comment could sit there for as long as the author went without saving and its roster went stale in
    // the meantime. A comment is now written at the moment it is made, carrying the roster that was
    // current when it was made, so there is no staleness window left to correct — and a later publish
    // sends the Forum the server itself served back, roster included.
  },
  extraReducers: (builder) => {
    builder.addCase(populateThreadComments.pending, (state, action) => {
      const proposalKey = commentStoreKey(action.meta.arg.thread.proposalId);
      state.loading[proposalKey] = true;
      state.error[proposalKey] = null;
    });
    builder.addCase(populateThreadComments.fulfilled, (state, action) => {
      const proposalKey = commentStoreKey(action.meta.arg.thread.proposalId);
      const forumId = action.meta.arg.thread.id?.value;
      if (forumId) state.forumLoads[forumId] = "loaded";

      state.loading[proposalKey] = false;
      let existingComments = state.entries[proposalKey] || [];

      for (const comment of action.payload) {
        if (existingComments.some((existingComment) => existingComment.id?.isEqualTo(comment.id))) {
          existingComments = existingComments.filter(
            (existingComment) => !existingComment.id?.isEqualTo(comment.id),
          );
        }
        existingComments.push(comment);
      }

      state.entries[proposalKey] = existingComments;
    });
    builder.addCase(populateThreadComments.rejected, (state, action) => {
      const proposalKey = commentStoreKey(action.meta.arg.thread.proposalId);
      // An older page that fails leaves the first page loaded.
      markForumLoadFailed(state, action.meta.arg.thread.id?.value);
      state.loading[proposalKey] = false;
      state.error[proposalKey] = action.error;
    });
    builder.addCase(populateThreadsComments.pending, (state, action) => {
      for (const proposalKey of requestedProposalKeys(action.meta.arg.threads)) {
        state.loading[proposalKey] = true;
        state.error[proposalKey] = null;
      }
    });
    builder.addCase(populateThreadsComments.fulfilled, (state, action) => {
      for (const proposalKey of requestedProposalKeys(action.meta.arg.threads)) {
        state.loading[proposalKey] = false;
        // An empty answer is still a loaded answer. Without this the pane's "not loaded yet" test
        // (`entries === undefined`) stays true forever for a negotiation whose threads carry no
        // comments, and the fallback effect re-dispatches a load on every render.
        state.entries[proposalKey] ??= [];
      }
      for (const comment of action.payload.comments) {
        upsertCommentInEntries(state, comment);
      }
      for (const forumId of action.payload.loadedForumIds) {
        state.forumLoads[forumId] = "loaded";
      }
    });
    builder.addCase(populateThreadsComments.rejected, (state, action) => {
      for (const proposalKey of requestedProposalKeys(action.meta.arg.threads)) {
        state.loading[proposalKey] = false;
        state.error[proposalKey] = action.error;
      }
      for (const thread of action.meta.arg.threads) {
        markForumLoadFailed(state, thread.id?.value);
      }
    });
    builder.addCase(markCommentsRead.fulfilled, (state, action) => {
      for (const comment of action.payload) {
        applyReadReceiptsInEntries(state, comment);
      }
    });
    // A creation's answer can land after the answer to a write made against the very comment it created,
    // because the broadcast of the creation reaches the pane first. See applyCreationAnswerInEntries.
    for (const creationThunk of [postComment, saveDraftComment]) {
      builder.addCase(creationThunk.fulfilled, (state, action) => {
        if (action.payload) {
          applyCreationAnswerInEntries(state, action.payload);
        }
      });
    }
    // Every other comment write is a single comment's own action and answers with the server copy of it;
    // upsert that so its id, content and published state stay authoritative. A retract that removed the
    // row for the audience still returns the author's own (now-draft) copy.
    for (const singleCommentThunk of [editComment, publishComment, unpublishComment]) {
      builder.addCase(singleCommentThunk.fulfilled, (state, action) => {
        if (action.payload) {
          upsertCommentInEntries(state, action.payload);
        }
      });
    }
    // The server marked every unread comment in one of the pane's conversations read, loaded or not, and
    // answers with a count. The loaded ones are drawn read here. Only the read receipts change, so a
    // write still in flight against one of these comments keeps the rest of it.
    builder.addMatcher(messagingApi.endpoints.markForumRead.matchFulfilled, (state, action) => {
      const { forumId, readerId } = action.meta.arg.originalArgs;
      markLoadedThreadReadInEntries(state, forumId, readerId);
    });
  },
});

export const { addComment, addCreatedComment, removeCommentById, removeComment, clearUnsavedCommentsByProposalId } =
  commentsSlice.actions;

const EMPTY_COMMENTS: Comment[] = [];

/**
 * Every loaded comment for a proposal/negotiation — which, now that a proposal has one comment thread
 * per audience rather than one per field, is simply that proposal's whole conversation. `undefined`
 * means "not loaded yet", which is how the pane tells an empty conversation from an unfetched one.
 */
export const getCommentsByProposal =
  (proposalId?: Guid | null) =>
  (state: RootState): Comment[] | undefined => {
    if (!proposalId) return EMPTY_COMMENTS;
    return state.comments.entries[commentStoreKey(proposalId)] ?? undefined;
  };

/**
 * How many of a proposal's comments the actor has staged and not yet sent. Memoized on
 * `state.comments.entries` so a subscribing page only re-renders when comments actually change.
 */
export const getPendingCommentsCount = (proposalId?: Guid | null) => {
  if (!proposalId) {
    return () => 0;
  }

  const cachedSelector = pendingCommentsCountSelectorCache.get(proposalId.value);
  if (cachedSelector) {
    return cachedSelector;
  }

  const selector = createSelector([selectCommentEntries], (entries): number =>
    countMatchingComments(entries[proposalId.value], (comment) => comment.isPending),
  );

  pendingCommentsCountSelectorCache.set(proposalId.value, selector);
  return selector;
};
export const getIsLoadingComments = (proposalId?: Guid | null) => (state: RootState) => {
  if (!proposalId) return false;
  return state.comments.loading[commentStoreKey(proposalId)] ?? false;
};
/**
 * Whether the last populate attempt for this proposal failed. The Comments effect reads this so a
 * rejected load is treated as a settled (once-only) attempt rather than re-dispatched forever: a
 * rejected `populateThreadComments` never writes `entries` (so `getCommentsByProposal` stays
 * `undefined`) and clears `loading`, which without this guard would satisfy the effect's re-populate
 * condition on every render and churn the loader indefinitely.
 */
export const getCommentsLoadError =
  (proposalId?: Guid | null) =>
  (state: RootState): SerializedError | null => {
    if (!proposalId) return null;
    return state.comments.error[commentStoreKey(proposalId)] ?? null;
  };
const unpublishedDraftCommentsSelectorCache = new Map<string, (state: RootState) => Comment[]>();

/**
 * The author-only draft comments a proposal is holding — only ever non-empty with drafting configured
 * on ({@link proposalCommentsPublishOnPost} false), since a comment otherwise posts published. A comment
 * the author retracted is excluded: it is a draft, but it is one they deliberately took back, and it is
 * only ever re-delivered by the row's own Publish button.
 *
 * Nothing about a proposal save reads this. It exists so a surface can say how much of the actor's own
 * conversation is still private to them; a save neither reports it, offers to publish it, nor waits for
 * it.
 *
 * Memoized on `state.comments.entries`. An unmemoized `.filter()` returns a fresh array identity on
 * every call, so every unrelated dispatch (a keystroke, a read receipt, an Ably push) looked like a
 * change to the subscribing page and re-rendered the whole proposal editor.
 */
export const getUnpublishedDraftComments = (proposal?: Proposal | null): ((state: RootState) => Comment[]) => {
  const proposalId = proposal?.id;
  if (!proposalId?.value) return () => EMPTY_COMMENTS;

  const cachedSelector = unpublishedDraftCommentsSelectorCache.get(proposalId.value);
  if (cachedSelector) return cachedSelector;

  const selector = createSelector([selectCommentEntries], (entries): Comment[] => {
    const drafts = Object.values(entries)
      .flat()
      .filter((comment) => comment.thread.proposalId.isEqualTo(proposalId) && isPublishableDraft(comment));
    return drafts.length > 0 ? drafts : EMPTY_COMMENTS;
  });

  unpublishedDraftCommentsSelectorCache.set(proposalId.value, selector);
  return selector;
};

export default commentsSlice;
